/* ── What an email address must be before Meridian keeps it ─────────────
 *
 * This file is kept byte for byte identical in `packages/ui/src/email-rules.ts`,
 * so the screen that takes an address and the server that keeps it answer
 * the same way (email-rules.test.ts in the API holds the two together).
 *
 * The round of 1 November (P-10): the public onboarding form took
 * "ssali.joshua.@dhs.africa", a dot right before the @, because every check
 * read only "something, an @, something, a dot, something". The mail
 * service refused it, and Edscan Medical Services' provisioning stopped on
 * it. These are the shapes a mail service refuses: spaces, not exactly one
 * @, a dot at either end of the name or two in a row, a domain with no
 * ending, a label that starts or ends with a hyphen. Plus-addressing, long
 * endings and international domains stay allowed.
 */

const LOCAL_CHARS = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~.\- -￿]+$/;
const LABEL = /^[A-Za-z0-9 -￿](?:[A-Za-z0-9\- -￿]*[A-Za-z0-9 -￿])?$/;
const ENDING = /^(?:[A-Za-z -￿]{2,}|xn--[A-Za-z0-9-]+)$/;

/** What is wrong with an address, in one sentence for the screen, or null
 *  when it is one a mail service takes. */
export function emailProblem(raw: string): string | null {
  const value = raw.trim();
  if (value === '') return 'Enter an email address.';
  if (/\s/.test(value)) return 'An email address has no spaces.';
  const at = value.split('@');
  if (at.length !== 2) return 'An email address has one @, as in name@example.com.';
  const [local, domain] = at as [string, string];
  if (local === '') return 'Write the name before the @.';
  if (domain === '') return 'Write the domain after the @, as in name@example.com.';
  if (local.length > 64 || value.length > 254) return 'This email address is too long.';
  if (local.endsWith('.')) return 'There is a dot just before the @. Take it out.';
  if (local.startsWith('.')) return 'An email address does not start with a dot.';
  if (local.includes('..') || domain.includes('..')) return 'There are two dots in a row. Take one out.';
  if (!LOCAL_CHARS.test(local)) return 'The name before the @ has a character email does not allow.';
  if (domain.startsWith('.') || domain.endsWith('.')) return 'The domain after the @ starts or ends with a dot. Take it out.';
  const labels = domain.split('.');
  if (labels.length < 2) return 'The domain after the @ needs its ending, as in .com or .africa.';
  if (!labels.every((label) => LABEL.test(label))) return 'The domain after the @ is not one mail can reach.';
  if (!ENDING.test(labels[labels.length - 1]!)) return 'The domain after the @ needs its ending, as in .com or .africa.';
  return null;
}

export function isStrictEmail(raw: string): boolean {
  return emailProblem(raw) === null;
}
